import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    fullName: '',
    role: 'user',
    organizationId: ''
  });
  const [orgForm, setOrgForm] = useState({ name: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, orgsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/organizations')
      ]);

      setUsers(usersRes.data.users);
      setOrganizations(orgsRes.data.organizations);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      password: '',
      fullName: '',
      role: 'user',
      organizationId: ''
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      fullName: user.full_name,
      role: user.role || 'user',
      organizationId: user.organization_id || '',
      password: '' // Don't pre-fill password
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update user
        await api.put(`/admin/users/${editingUser.id}`, {
          fullName: userForm.fullName,
          role: userForm.role,
          organizationId: userForm.organizationId || null,
          isActive: editingUser.is_active
        });
        toast.success('User updated successfully');
      } else {
        // Create user
        await api.post('/admin/create-user', userForm);
        toast.success('User created successfully');
      }

      setShowUserModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to save user');
    }
  };

  const handleToggleUserActive = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}`, { isActive: !isActive });
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();

    try {
      await api.post('/organizations', orgForm);
      toast.success('Organization created successfully');
      setShowOrgModal(false);
      setOrgForm({ name: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create organization');
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'badge-danger',
      dispatcher: 'badge-warning',
      supervisor: 'badge-info',
      user: 'badge-secondary'
    };
    return roleColors[role] || 'badge-secondary';
  };

  return (
    <div>
      <div className="header-actions">
        <h2>User & Organization Management</h2>
        <div>
          <button onClick={() => setShowOrgModal(true)} className="btn btn-secondary" style={{ marginRight: '1rem' }}>
            + Create Organization
          </button>
          <button onClick={handleCreateUser} className="btn btn-primary">
            + Create User
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3>Organizations ({organizations.length})</h3>
        <div className="org-list">
          {organizations.map((org) => (
            <div key={org.id} className="org-item">
              <div>
                <strong>{org.name}</strong>
                <span className="text-muted"> ({org.user_count} users)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3>Users ({users.length})</h3>
        <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.full_name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`badge ${getRoleBadge(user.role)}`}>
                    {user.role || 'user'}
                  </span>
                </td>
                <td>{user.organization_name || 'None'}</td>
                <td>
                  <span className={`badge badge-${user.is_active ? 'success' : 'danger'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditUser(user)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => handleToggleUserActive(user.id, user.is_active)}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  {!user.is_admin && (
                    <button
                      className="btn btn-sm btn-danger"
                      style={{ marginLeft: '0.5rem' }}
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>
            <form onSubmit={handleSaveUser}>
              {!editingUser && (
                <>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      required
                      minLength="8"
                    />
                    <small>Minimum 8 characters</small>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={userForm.fullName}
                  onChange={(e) => setUserForm({ ...userForm, fullName: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  className="form-control"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  required
                >
                  <option value="user">User (Standard)</option>
                  <option value="supervisor">Supervisor (Multi-group access)</option>
                  <option value="dispatcher">Dispatcher (Org Admin)</option>
                  <option value="admin">Admin (Full Access)</option>
                </select>
                <small>
                  <strong>User:</strong> Access to own group only<br />
                  <strong>Supervisor:</strong> Access to multiple subgroups<br />
                  <strong>Dispatcher:</strong> Can create/manage users in their org<br />
                  <strong>Admin:</strong> Full system access
                </small>
              </div>

              <div className="form-group">
                <label>Organization</label>
                <select
                  className="form-control"
                  value={userForm.organizationId}
                  onChange={(e) => setUserForm({ ...userForm, organizationId: e.target.value })}
                >
                  <option value="">No Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'} User
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Organization Modal */}
      {showOrgModal && (
        <div className="modal-overlay" onClick={() => setShowOrgModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Organization</h3>
            <form onSubmit={handleCreateOrg}>
              <div className="form-group">
                <label>Organization Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowOrgModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .header-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .org-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        .org-item {
          padding: 1rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          background-color: #f8f9fa;
        }
        .text-muted {
          color: #6c757d;
          font-size: 0.9em;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          padding: 30px;
          border-radius: 8px;
          min-width: 500px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }
        .form-group small {
          display: block;
          margin-top: 5px;
          color: #6c757d;
          font-size: 0.85em;
        }
        .form-control {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
          justify-content: flex-end;
        }
        .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.85em;
          font-weight: 500;
        }
        .badge-danger {
          background-color: #dc3545;
          color: white;
        }
        .badge-warning {
          background-color: #ffc107;
          color: #000;
        }
        .badge-info {
          background-color: #17a2b8;
          color: white;
        }
        .badge-secondary {
          background-color: #6c757d;
          color: white;
        }
        .badge-success {
          background-color: #28a745;
          color: white;
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};

export default UserManagement;
